import axios from "axios";
import HostService from "./host.service";
import AuthService from "./auth.service";

class NotesService {

    /**
     * Registrar Note (incidencias)
     * @param note
     * @returns {Promise<AxiosResponse<any>>}
     */
    static registerNotes(note) {
        return axios.post(HostService.getHost() + 'notes',
            {
                "serie": note.serie?.toString(),
                "albaran": note.albaran?.toString(),
                "id_empleado": note.id_empleado,
                "nombre_empleado": note.nombre_empleado,
                "note": note.note,
                "origen": note.origen,
                "estado": note.estado,
                "codigo_articulo": note.codigo_articulo,
                "tipo_note": note.tipo_note,
            }, AuthService.setToken())
            .then((response) => {
                return response.data;
            })
            .catch((error) => {
                console.log(error.message);
                throw error;
            });
    }

    /**
     * Registrar Note Comentarios (incidencias)
     * @param note
     * @returns {Promise<AxiosResponse<any>>}
     */
     static registerNotesComentarios(note) {
        // console.log('registerNotesComentario', note)

        const formData = new FormData();

        // Añadir campos de texto
        formData.append("id_note", note.id_note);
        formData.append("comentario", note.note);
        formData.append("nombre_empleado", note.nombre_empleado);

        // Añadir archivos (si los hay)
        if (note.imagenes && note.imagenes.length > 0) {
            note.imagenes.forEach(file => {
                formData.append('imagenes[]', file); // nombre esperado por el backend
            });
        }

        // Definir cabeceras
        // IMPORTANTE: NO establecer Content-Type manualmente con FormData
        // Axios lo establece automáticamente con el boundary correcto
        const config = {
            headers: {
                ...AuthService.setToken().headers,
            }
        };

        return axios.post(
            HostService.getHost() + 'notes/comentario',
            formData,
            config
        )
            .then(response => response.data)
            .catch(error => {
                console.log(error.message);
                throw error;
            });
    }

    /**
     * Listar incidencias con filtros opcionales (endpoint refactorizado, DTO camelCase)
     * Cada incidencia llega enriquecida con los datos de factura de Sage (campo invoice)
     * @param {Object} params - Filtros opcionales
     * @param {string} params.estado - Estados CSV: 1 (abierta), 2 (en proceso), 3 (cerrada). Ej: '1,2'
     * @param {string} params.origen - Origen de la incidencia (sales, purchases, sticker, inventario, nave2)
     * @param {number} params.ejercicio - Año fiscal (2000-2100)
     * @param {string} params.tipoNote - Tipo de incidencia
     * @returns {Promise<Array>} Lista de incidencias en camelCase
     */
    static getIncidencias(params = {}) {
        return axios.get(HostService.getHost() + 'incidencias', { ...AuthService.setToken(), params })
            .then((response) => {
                return response.data.data;
            })
            .catch((error) => {
                console.log(error.message);
                throw error;
            });
    }

    /**
     * Contar incidencias que cumplen los filtros indicados (sin cargar los datos)
     * @param {Object} params - Filtros opcionales
     * @param {string} params.estado - Estados CSV: 1 (abierta), 2 (en proceso), 3 (cerrada). Ej: '1,2'
     * @param {string} params.origen - Origen de la incidencia (almacen, transporte, cliente, sistema, nave2)
     * @param {number} params.ejercicio - Año fiscal (2000-2100)
     * @param {string} params.tipoNote - Tipo de incidencia (incidencia, observacion, reclamo)
     * @returns {Promise<number>} Número de incidencias que cumplen los filtros
     */
    static countIncidencias(params = {}) {
        return axios.get(HostService.getHost() + 'incidencias/count', { ...AuthService.setToken(), params })
            .then((response) => {
                return response.data.data.count;
            })
            .catch((error) => {
                console.log(error.message);
                throw error;
            });
    }

    /**
     * Obtener etiquetas
     * @param id_agency
     * @returns {Promise<AxiosResponse<any>>}
     */
     static getNoteByNum(serie, albaran, origen) {
        return axios.get(HostService.getHost() + 'note/' + (serie === null ? 'undefined' : serie) + '/' + albaran + '/' + origen, AuthService.setToken())
            .then((response) => {
                return response.data;
            })
            .catch((error) => {
                console.log(error.message);
                throw error;
            });
    }

    /**
     * Obtener etiquetas
     * @param id_agency
     * @returns {Promise<AxiosResponse<any>>}
     */
    static getNoteByReferencia(referencia, origen, estado) {
        let url = `${HostService.getHost()}note/${referencia}/${origen}`;

        // Añadir el parámetro de query 'estado' si está definido
        if (estado !== undefined && estado !== null) {
            const queryParams = new URLSearchParams({ estado });
            url += `?${queryParams.toString()}`;
        }

        return axios.get(url, AuthService.setToken())
            .then((response) => {
                return response.data;
            })
            .catch((error) => {
                console.log(error.message);
                throw error;
            });
    }

    /**
     * Borrar incidencia
     * @param id_order
     * @returns {Promise<AxiosResponse<any>>}
     */
    static deleteNote(serie, order, counter, year) {
        return axios.delete(HostService.getHost() + 'etiquetas/' + serie + '/' + order + '?contador=' + counter + '&ejercicio='+year, AuthService.setToken())
            .then((response) => {
                return response.data;
            })
            .catch((error) => {
                console.log(error.message);
                throw error;
            });
    }

    /**
     * Modificar incidencia
     * @returns {Promise<AxiosResponse<any>>}
     */
     static updateNote(note) {
        return axios.put(HostService.getHost() + 'notes/'+note.id_note, { 
            note: note.note, 
            estado: note.estado,  
        }, AuthService.setToken())
            .then((response) => {
                return response.data;
            })
            .catch((error) => {
                console.log(error.message);
                throw error;
            });
    }

    static getInfoInvoice(serie, albaran, ejercicio) {
        return axios.get(HostService.getHost() + 'invoice/'+serie + '/' + albaran, AuthService.setToken())
            .then((response) => {
                return response.data;
            })
            .catch((error) => {
                console.log(error.message);
                throw error;
            });
    }

}

export default NotesService;